// Left-nav item registry (BUILD_SPEC.md §11). Role gates mirror §6's route-group
// matrix; an item with no `roles` is visible to every role, including READ_ONLY.
//
// Grouped-sections shape (Unleashed-parity chrome, Stuart ruling 2026-08-24):
// Stuart's team runs Unleashed Software today and wants the same navigation
// ergonomics — a collapsible section tree (Inventory ▸ Transactions / Products)
// rather than a flat list. `children` mirrors each screen-group layout route's
// OWN tab bar verbatim (routes/_authenticated/<group>.tsx's `TABS` — read, not
// invented), so the nav tree and the in-screen tab row never drift apart. A
// section with no `children` (Home, Reports, Stores & Transfers) has none
// because its layout route has no persistent tab bar of its own (Reports: 13
// sub-pages behind a breadcrumb, not a tab row; Stores & Transfers: a pathless
// `<Outlet/>`) — inventing children for those would show tabs that don't exist.
import type { Icon } from '@tabler/icons-react';
import {
  IconChartBar,
  IconGauge,
  IconHelp,
  IconHome,
  IconPackage,
  IconPackages,
  IconReceipt,
  IconSearch,
  IconSettings,
  IconStack2,
  IconTruckDelivery,
} from '@tabler/icons-react';
import type { Role } from '@zebra/core';

/** A section's sub-item — the section layout route's own tab. No icon (matches
 * Unleashed: sub-items are plain text rows, only the section header carries an
 * icon). */
export interface NavChild {
  label: string;
  to: string;
}

export interface NavSection {
  label: string;
  to: string;
  icon: Icon;
  /** Omit to show for every role. Gates the whole section — children have no
   * gate of their own, they inherit this one (BUILD_SPEC.md §6 gates by
   * screen-group, not by individual tab). */
  roles?: Role[];
  children?: NavChild[];
}

export const NAV_SECTIONS: NavSection[] = [
  { label: 'Home', to: '/', icon: IconHome },
  // Front-and-centre per Stuart's ruling 2026-08-26 (verbatim: "not having a
  // product page front and center on a stock management system seems like a
  // fairly basic oversight") — immediately after Home, ahead of every
  // transactional section. List + drill-down only, no persistent tab row of
  // its own, so (unlike every section below) it carries no `children` — the
  // NAV_SECTIONS<->TABS parity test (nav.test.ts) only asserts sections that
  // DO have children, so a childless section needs no matching case there.
  { label: 'Products', to: '/products', icon: IconPackages },
  {
    label: 'Purchasing & Intake',
    to: '/purchasing-intake',
    icon: IconTruckDelivery,
    roles: ['OPERATIONS', 'ADMIN'],
    // Verbatim from purchasing-intake.tsx's TABS (label + to).
    children: [
      { label: 'Receive a load', to: '/purchasing-intake' },
      { label: 'Purchase orders', to: '/purchasing-intake/purchase-orders' },
      { label: 'Suppliers', to: '/purchasing-intake/suppliers' },
      { label: 'Contracts', to: '/purchasing-intake/contracts' },
      { label: 'Reorder', to: '/purchasing-intake/reorder' },
      { label: 'Receipts', to: '/purchasing-intake/receipts' },
    ],
  },
  {
    label: 'Production & Packing',
    to: '/production-packconv',
    icon: IconStack2,
    roles: ['OPERATIONS', 'ADMIN'],
    // Verbatim from production-packconv.tsx's TABS.
    children: [
      { label: 'Record a shift', to: '/production-packconv' },
      { label: 'Quick conversion', to: '/production-packconv/quick' },
      { label: 'Kiln batches', to: '/production-packconv/batches' },
      { label: 'Runs', to: '/production-packconv/runs' },
      { label: 'Pack conversion', to: '/production-packconv/pack-conversion' },
      { label: 'Stages & recipes', to: '/production-packconv/stages' },
    ],
  },
  {
    label: 'Silo & Counts',
    to: '/silo-counts',
    icon: IconGauge,
    roles: ['OPERATIONS', 'ADMIN'],
    // Verbatim from silo-counts.tsx's TABS, counts-first order (2026-08-24
    // ruling: Howick has no silos, so Stock counts leads, not Silo status).
    children: [
      { label: 'Stock counts', to: '/silo-counts/counts' },
      { label: 'Silo status', to: '/silo-counts/status' },
      { label: 'Calibration', to: '/silo-counts/calibration' },
    ],
  },
  {
    label: 'Sales & Invoicing',
    to: '/sales-invoicing',
    icon: IconReceipt,
    // OPERATIONS included (audit fix 2026-07-07): the API genuinely allows
    // OPERATIONS on dispatch create/post/reverse (`requireRole('OPERATIONS',
    // 'SALES_ACCOUNTS', 'ADMIN')`) and the Home "Dispatch a load" task button
    // (lib/taskButtons.ts) routes OPERATIONS here — nav mirrors API capability;
    // the screen group's own tabs/buttons gate the invoice-only actions.
    roles: ['OPERATIONS', 'SALES_ACCOUNTS', 'ADMIN'],
    // Verbatim from sales-invoicing.tsx's TABS.
    children: [
      { label: 'Dispatch a load', to: '/sales-invoicing' },
      { label: 'Dispatch without weighbridge', to: '/sales-invoicing/dispatch-manual' },
      { label: 'Sales orders', to: '/sales-invoicing/sales-orders' },
      { label: 'Dispatches', to: '/sales-invoicing/dispatches' },
      { label: 'Raise invoices', to: '/sales-invoicing/raise' },
      { label: 'Invoices', to: '/sales-invoicing/invoices' },
      { label: 'Credit notes', to: '/sales-invoicing/credit-notes' },
      { label: 'Customers & prices', to: '/sales-invoicing/customers' },
    ],
  },
  {
    label: 'Stores & Transfers',
    to: '/stores-transfers',
    icon: IconPackage,
    roles: ['OPERATIONS', 'ADMIN'],
    // No children: stores-transfers.tsx is a pathless `<Outlet/>` layout with
    // no TABS of its own to mirror.
  },
  { label: 'Reports', to: '/reports', icon: IconChartBar },
  // No children: reports.tsx is a pathless `<Outlet/>` — 13 sub-pages behind
  // per-page "back to hub" breadcrumbs, not a persistent tab bar.
];

/** Per-tenant runtime filter (Stuart ruling 2026-08-26: silos are a per-tenant
 * module, default OFF — see lib/tenantFeatures.ts). NAV_SECTIONS itself stays
 * the static, full-fidelity list nav.test.ts pins against each layout's own
 * TABS verbatim (this file's module comment) — inlining the gate into the
 * array would break that parity contract for a section that still legitimately
 * has three tabs when silos ARE on. This is where the "off" case actually
 * collapses "Silo & Counts" down to the one screen that survives: renamed to
 * "Stock counts", pointed straight at the counts register, with no `children`
 * (there's nothing left to switch between). Shell.tsx calls this before its
 * own role filter; every other section passes through unchanged. */
export function navSectionsForTenant(features: { silosEnabled: boolean }): NavSection[] {
  if (features.silosEnabled) return NAV_SECTIONS;
  return NAV_SECTIONS.map((section) =>
    section.label === 'Silo & Counts'
      ? { ...section, label: 'Stock counts', to: '/silo-counts/counts', children: undefined }
      : section,
  );
}

/** Pinned to the navbar bottom, below the scrollable section tree (Unleashed
 * parity: Tasks/Help/Settings sit as a fixed utility group under the main nav,
 * not mixed into it). Same `NavSection` shape as the main tree but rendered as
 * plain compact links — none of the three has sub-tabs worth a chevron. */
export const UTILITY_ITEMS: NavSection[] = [
  { label: 'Search', to: '/search', icon: IconSearch },
  { label: 'Help', to: '/help', icon: IconHelp },
  {
    label: 'Settings',
    to: '/settings',
    icon: IconSettings,
    roles: ['ADMIN'],
    // Verbatim from routes/_authenticated/settings.tsx's own TABS (label + to)
    // — same mirroring discipline as the main NAV_SECTIONS above, so Products'
    // sidebar visibility (Stuart's direct ask: "where are my ... product
    // lists?") stays locked to the real tab bar rather than drifting.
    children: [
      { label: 'Tenant', to: '/settings' },
      { label: 'Sites & locations', to: '/settings/sites-locations' },
      { label: 'Cost centres', to: '/settings/cost-centres' },
      { label: 'Products', to: '/settings/products' },
      { label: 'Users & roles', to: '/settings/users' },
      { label: 'Xero', to: '/settings/xero' },
      { label: 'Importer', to: '/settings/importer' },
    ],
  },
];
